import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import TemplatesApi from "../../../api/templates-api";

const triggerJsonDownload = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
  };
};

export const useTemplateAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  const fetchAnalytics = useCallback(
    async (templateId, { page = 1, status, startDate, endDate } = {}) => {
      try {
        setLoading(true);
        const params = {
          templateId,
          startDate: startDate || dateRange.startDate,
          endDate: endDate || dateRange.endDate,
          page,
          limit: 20,
        };
        if (status) params.status = status;

        const { data: response } = await TemplatesApi.private.getTemplateAnalytics(params);
        if (response && !response.error) {
          setAnalyticsData(response.data);
        } else {
          toast.error("Failed to fetch analytics");
        }
      } catch (error) {
        console.error("Error fetching analytics:", error);
        toast.error("An error occurred while fetching analytics");
      } finally {
        setLoading(false);
      }
    },
    [dateRange]
  );

  const exportAnalytics = useCallback(
    async (templateId, templateName, { status, startDate, endDate } = {}) => {
      try {
        setExporting(true);
        const params = {
          templateId,
          startDate: startDate || dateRange.startDate,
          endDate: endDate || dateRange.endDate,
          status: status || undefined,
        };

        const { data: response } = await TemplatesApi.private.exportTemplateAnalytics(params);
        if (response && !response.error) {
          const safeName = (templateName || templateId).replace(/[^a-z0-9_-]/gi, "_");
          triggerJsonDownload(response.data, `analytics_${safeName}_${params.startDate}_${params.endDate}.json`);
        } else {
          toast.error("Failed to export analytics");
        }
      } catch (error) {
        console.error("Error exporting analytics:", error);
        toast.error("An error occurred while exporting analytics");
      } finally {
        setExporting(false);
      }
    },
    [dateRange]
  );

  const resetState = useCallback(() => {
    setAnalyticsData(null);
    setCurrentPage(1);
    setStatusFilter("");
    setDateRange(getDefaultDateRange());
  }, []);

  return {
    analyticsData,
    loading,
    exporting,
    currentPage,
    setCurrentPage,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    fetchAnalytics,
    exportAnalytics,
    resetState,
  };
};
